import type { Experience } from 'sr-types/lib/person/v1/graphql';
import type { WorkEntryRow } from '../types';
import {
    buildRowsWithStableIds,
    formatYearRangeFromDates,
    getYearsFromDateRange,
    parseRoleLabels
} from './shared';

function getExperienceBaseId(experience: Experience): string {
    const organizationId = experience.organizationReference?.id ?? experience.experienceName ?? '';
    const roles = (experience.professionalRoles?.filter(Boolean) as string[]) ?? [];
    const start = experience.dateRange?.start ?? '';
    return `${organizationId}|${roles.join(',')}|${start}`;
}

function formatExperienceSecondaryLine(experience: Experience): string | undefined {
    const parts = [experience.jobType, experience.title, experience.department].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : undefined;
}

function toExperienceRow(experience: Experience, id: string): WorkEntryRow<Experience> {
    const roleLabels = parseRoleLabels(experience.professionalRoles);
    const primaryTitle = experience.experienceName ?? experience.organizationReference?.label ?? '';
    const secondaryLine = formatExperienceSecondaryLine(experience);
    const searchText = [
        primaryTitle,
        experience.jobType,
        experience.title,
        experience.department,
        ...roleLabels
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return {
        id,
        source: experience,
        primaryTitle,
        secondaryLine,
        roleLabels,
        yearRange: formatYearRangeFromDates(experience.dateRange?.start, experience.dateRange?.end) || undefined,
        location: experience.location?.label?.trim() || undefined,
        typeValue: experience.jobType || undefined,
        years: getYearsFromDateRange(experience.dateRange?.start, experience.dateRange?.end),
        searchText,
        isKey: !!experience.keyExperience
    };
}

export function buildExperienceRows(experiences: ReadonlyArray<Experience>): WorkEntryRow<Experience>[] {
    return buildRowsWithStableIds(experiences, getExperienceBaseId, toExperienceRow);
}
